import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Password, TextInput, Typography } from '@guildeducationinc/recess';
import ArrowForwardIcon from '@guildeducationinc/recess/icons/ArrowForwardIcon';
import { ClientHeader } from '@src/components/LoginForm/ClientHeader';
import { useAuth } from '@src/hooks/useAuth';
import { getRegionAndClient, Provider, TrackableButton, trackEvent, useLocale, } from 'pe-core-frontend';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClientWelcomeWrapper } from '../ClientWelcomeWrapper';
import { LoginSignupTab } from '../LoginSignupTab';
import styles from './LoginForm.module.scss';
const DEFAULT_CREDS = { username: '', password: '' };
export const LoginForm = ({ directLoginConfig, otherConfigs }) => {
    const navigate = useNavigate();
    const { t } = useLocale();
    const { currentUser, login } = useAuth();
    const [creds, setCreds] = useState(DEFAULT_CREDS);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showMfa, setShowMfa] = useState(false);
    const validate = () => {
        const fieldErrors = {};
        if (creds.username.trim() === '') {
            fieldErrors.username = t('{field} is required', { values: { field: t('Email') } });
        }
        if (creds.password.trim() === '') {
            fieldErrors.password = t('{field} is required', { values: { field: t('Password') } });
        }
        if (showMfa && !creds.mfaCode) {
            fieldErrors.mfaCode = t('{field} is required', { values: { field: t('MFA Code') } });
        }
        setFieldErrors(fieldErrors);
        if (fieldErrors.username || fieldErrors.password)
            return false;
        return true;
    };
    const handleLoginClick = (config) => {
        const returnTo = window.location.pathname + window.location.search;
        const { region, client } = getRegionAndClient();
        switch (config.provider) {
            case Provider.SAML:
                window.location.href = `/${region}/${client}/auth/saml/login?return_to=${returnTo}`;
                break;
            case Provider.GUILD_AUTH:
                // when using Guild Auth, do not include a client in saml URL path, but do include client in the return_to
                window.location.href = `/${region}/auth/saml/login?return_to=${returnTo}`;
                break;
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validate())
            return;
        try {
            setSubmitting(true);
            const result = await login(creds.username.trim(), creds.password.trim(), creds.mfaCode?.trim());
            if (result.errors) {
                setError(t(result.errors[0].message));
                return;
            }
            const { success, mfaRequired, message } = result.data.login;
            if (success) {
                trackEvent('AccountLoginSuccess');
                navigate('/');
                return;
            }
            if (mfaRequired) {
                setShowMfa(true);
            }
            if (message && message?.length > 0) {
                trackEvent('AccountLoginFail');
            }
            setError(t(message));
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setSubmitting(false);
        }
    };
    if (currentUser) {
        return null;
    }
    return (_jsx("div", { className: styles.loginForm, children: _jsxs("div", { className: styles.container, children: [_jsx(ClientHeader, {}), _jsx(ClientWelcomeWrapper, { children: _jsx("fieldset", { disabled: submitting, children: _jsxs("form", { noValidate: true, className: styles.form, onSubmit: handleSubmit, children: [directLoginConfig && _jsx(LoginSignupTab, { selectedTab: 'login' }), _jsx(Typography, { variant: 'h1', gutterBottom: 'md', children: t('Welcome back!') }), directLoginConfig && (_jsxs(_Fragment, { children: [_jsx(Alert, { hideClose: true, display: !!error, type: 'danger', className: 'margin--bottom_24', children: error }), _jsx(TextInput, { label: t('Account email'), required: true, value: creds.username, onChange: e => setCreds({ ...creds, username: e.target.value }), errors: fieldErrors.username, "data-testid": 'usernameInput' }), _jsx(Password, { label: t('Password'), required: true, name: 'password', value: creds.password, onChange: e => setCreds({ ...creds, password: e.target.value }), errors: fieldErrors.password, "data-testid": 'passwordInput' }), showMfa && (_jsx(TextInput, { label: t('MFA Code'), required: true, value: creds.mfaCode, onChange: e => setCreds({ ...creds, mfaCode: e.target.value }), errors: fieldErrors.mfaCode, "data-testid": 'mfaCodeInput' })), _jsxs("div", { className: styles.actions, children: [_jsx("div", { className: 'text-right', children: _jsx(TrackableButton, { btnType: 'link', enText: 'Forgot password', tag: 'form', component: Link, to: '/auth/forgot-password', "data-testid": 'forgotPasswordButton', children: t('Forgot password') }) }), _jsx(TrackableButton, { enText: 'Sign In', tag: 'form', type: 'submit', "data-testid": 'loginButton', disabled: submitting, children: t('Sign In') }, 'submit'), _jsxs("div", { className: 'flex gap-3 flexJustify--center', children: [_jsx(Typography, { variant: 'body1', weight: '700', children: t("Don't have an account?") }), _jsxs(TrackableButton, { btnType: 'link', enText: 'Sign up', tag: 'form', component: Link, to: '/auth/sign-up', "data-testid": 'signupButton', className: 'flex gap-1 flexItems--center', children: [t('Sign up'), " ", _jsx(ArrowForwardIcon, { size: 16 })] })] })] })] })), otherConfigs.length > 0 && (_jsxs("div", { className: styles.actions, children: [directLoginConfig && _jsx("div", { className: 'margin--auto', children: t('OR') }), otherConfigs.map(config => (_jsx(TrackableButton, { enText: `Log in with ${config.name}`, tag: 'form', onClick: () => handleLoginClick(config), disabled: submitting, "data-testid": 'loginButtonSso', children: config.provider === Provider.GUILD_AUTH
                                                ? t('Member Login')
                                                : t('Log in with {config_name}', {
                                                    values: { config_name: config.name },
                                                }) }, config.id)))] }))] }) }) })] }) }));
};
